import React, { useEffect, useRef, useState } from "react";
import { Field, Form, FormSpy } from "react-final-form";
import {
  Button,
  HeaderDescription,
  ToggleSwitch,
  Tooltip,
  ToolTipPosition,
} from "@pickit/pickit-components";

import { DetailPickerTypeProps } from "./types";
import { PickerType } from "../pickers/types";

import useValidationSchema from "hooks/useValidationSchema";

import PersonalData from "./personalData/PersonalData";
import AccountDataCO from "./accountData/CO/AccountDataCO";
import AccountDataMX from "./accountData/MX/AccountDataMX";
import AccountDataAR from "./accountData/AR/AccountDataAR";
import VehicleDataAR from "./vehicleData/AR/VehicleDataAR";
import VehicleDataCO from "./vehicleData/CO/VehicleDataCO";
import "pages/pickers/Pickers.scss";
import "pages/detailPicker/detailPicker.scss";
import "./i18n";
import { useTranslation } from "react-i18next";
import { CountryComponentType } from "utils/types";
import VehicleDataMX from "./vehicleData/MX/VehicleDataMX";
import { VehicleDataProps } from "./vehicleData/types";
import { AccountDataProps } from "./accountData/types";
import {
  MOTORIZED_VEHICLES_TAGS,
  VEHICLE_MOTORIZED_DOCUMENT_TAGS,
  VEHICLE_TAGS,
  VehicleTagType,
} from "utils/constants";
import { hasRequiredPickerFilesLoaded } from "reducers/detailPicker";
import { doesPickerFileRowIndicateUpload, normalizePickerFileTag } from "utils/files";
import type { PickerFileApiRow } from "utils/files";
import AccountDataCL from "./accountData/CL/AccountDataCL";
import VehicleDataCL from "./vehicleData/CL/VehicleDataCL";

type MotorizedVehicleFieldSnapshot = {
  patent: string;
  expirationDateDriverLicense: string | null;
  expirationDateIdentificationVehicle: string | null;
  expirationDatePolicyVehicle: string | null;
};

const snapshotFromVehicleValues = (
  v: PickerType["vehicle"] | undefined
): MotorizedVehicleFieldSnapshot => ({
  patent: v?.patent ?? "",
  expirationDateDriverLicense: v?.expirationDateDriverLicense ?? null,
  expirationDateIdentificationVehicle:
    v?.expirationDateIdentificationVehicle ?? null,
  expirationDatePolicyVehicle: v?.expirationDatePolicyVehicle ?? null,
});

const isMotorizedTypeSwap = (
  fromType: VehicleTagType | undefined,
  toType: VehicleTagType | undefined
): boolean =>
  fromType != null &&
  toType != null &&
  MOTORIZED_VEHICLES_TAGS.includes(fromType) &&
  MOTORIZED_VEHICLES_TAGS.includes(toType) &&
  fromType !== toType;

const isFromNonMotorizedToMotorized = (
  fromType: VehicleTagType | undefined,
  toType: VehicleTagType | undefined
): boolean =>
  fromType != null &&
  toType != null &&
  !MOTORIZED_VEHICLES_TAGS.includes(fromType) &&
  MOTORIZED_VEHICLES_TAGS.includes(toType);

const applyMotorizedVehicleFieldSnapshot = (
  form: { change: (name: string, value?: unknown) => void },
  snap: MotorizedVehicleFieldSnapshot
) => {
  form.change("vehicle.patent", snap.patent);
  form.change("vehicle.expirationDateDriverLicense", snap.expirationDateDriverLicense);
  form.change(
    "vehicle.expirationDateIdentificationVehicle",
    snap.expirationDateIdentificationVehicle
  );
  form.change("vehicle.expirationDatePolicyVehicle", snap.expirationDatePolicyVehicle);
};

const clearMotorizedVehicleFieldValues = (form: {
  change: (name: string, value?: unknown) => void;
}) => {
  applyMotorizedVehicleFieldSnapshot(form, {
    patent: "",
    expirationDateDriverLicense: null,
    expirationDateIdentificationVehicle: null,
    expirationDatePolicyVehicle: null,
  });
};

export const DetailPicker: React.FC<DetailPickerTypeProps> = ({
  actualPage,
  dirty,
  accountTypes,
  pendingUserAdminPicker,
  initialValues,
  setDirty,
  active,
  nameDisplay,
  phoneNumber,
  validationSchema,
  documentsSaveValidationSchema,
  wrongFiles,
  regions,
  tabs,
  countryCode,
  fiscalNumberRef,
  personalDataEmailRef,
  hasSaved,
  formRef,
  cancel,
  goBack,
  savePicker,
  changePage,
  getBankName,
  onSubmit,
  getWorkzones,
  setFiscalNumberDuplicated,
  setEmailOrUsernameDuplicated,
  contactPicker,
  setHasSaved,
  ensureMotorizedVehicleFileSlots,
  ...props
}) => {
  const { t } = useTranslation();
  const { id, vehicle, personalData, files } = pendingUserAdminPicker;

  const previousVehicleTypeRef = useRef<VehicleTagType | undefined>(
    initialValues?.vehicle?.type ?? vehicle?.type
  );
  const lastSyncedPickerIdRef = useRef<number | null>(null);
  const originalVehicleTypeFromServerRef = useRef<VehicleTagType | undefined>(
    undefined
  );
  const vehicleMotorizedFieldsByTypeRef = useRef<
    Partial<Record<VehicleTagType, MotorizedVehicleFieldSnapshot>>
  >({});
  const [showMotorizedFileReplaceReminder, setShowMotorizedFileReplaceReminder] =
    useState(false);
  const vehicleFormSpyHydratedForPickerIdRef = useRef<number | null>(null);
  const vehicleTypesResetInSessionRef = useRef<Set<VehicleTagType>>(new Set());

  useEffect(() => {
    const v = initialValues?.vehicle ?? vehicle;
    const typeNow = v?.type;
    if (lastSyncedPickerIdRef.current !== id) {
      lastSyncedPickerIdRef.current = id;
      vehicleFormSpyHydratedForPickerIdRef.current = null;
      vehicleMotorizedFieldsByTypeRef.current = {};
      vehicleTypesResetInSessionRef.current = new Set();
      setShowMotorizedFileReplaceReminder(false);
      originalVehicleTypeFromServerRef.current = typeNow as
        | VehicleTagType
        | undefined;
      if (typeNow && MOTORIZED_VEHICLES_TAGS.includes(typeNow)) {
        vehicleMotorizedFieldsByTypeRef.current[typeNow] =
          snapshotFromVehicleValues(v);
      }
    }
    previousVehicleTypeRef.current = typeNow;
    if (id > 0) {
      vehicleFormSpyHydratedForPickerIdRef.current = id;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, initialValues?.vehicle?.type, vehicle?.type]);

  const accountDataProps: AccountDataProps = {
    ...props,
    pickerId: id,
    isActivePicker: active,
    testId: "account-data",
    fiscalNumberRef,
    setFiscalNumberDuplicated,
    files: files.accountingData,
  };

  const accountData: CountryComponentType = {
    AR: <AccountDataAR {...accountDataProps} />,
    CO: <AccountDataCO {...accountDataProps} accountTypes={accountTypes} />,
    MX: <AccountDataMX {...accountDataProps} />,
    CL: <AccountDataCL {...accountDataProps} />,
  };

  return (
    <>
      <div className="header-container">
        <HeaderDescription
          headline={`${nameDisplay} - ID ${id}`}
          overline={`Reportes - Flota - ${active ? "pickers" : "Solicitudes pendientes"}` as string}
          testId="picker-title-name"
        />
        <Button
          testId="picker-button-back"
          onClick={() => goBack(true, dirty)}
          hierarchy="secondary"
          label={t("global:back")}
          size="medium"
          iconName="arrow-left"
        />
      </div>
      <div className="page-title-container">
        <h3
          data-testId="picker-title-account-data"
          className="detail-picker-section-title"
        >
          {t("picker:profileTitle")}
        </h3>
        <Button
          hierarchy="primary"
          label={t("picker:contactLabel")}
          disabled={!phoneNumber}
          onClick={contactPicker}
          iconName="whatsapp"
          testId={`picker-contact-button`}
          size="medium"
        />
      </div>
      <Form
        keepDirtyOnReinitialize
        onSubmit={(values) => onSubmit(values as PickerType)}
        initialValues={initialValues}
        validate={useValidationSchema(validationSchema)}
        mutators={{
          bankSearch: ([field, value], state, { changeValue }) => {
            const values = state.formState.values as PickerType;
            const bankIdentifier = values.accountingData?.account?.number;

            if (!bankIdentifier || bankIdentifier.length < 22)
              changeValue(state, "accountingData.bankName", () => "-");
            else {
              const prefix = bankIdentifier.substring(0, 3);
              getBankName(prefix);
            }
          },
          setValue: ([field, value], state, { changeValue }) => {
            changeValue(state, field, () => value);
          },
          setWorkzones: ([field, value], state, { changeValue }) => {
            changeValue(state, "desiredWorkzones", () => "");
            if ((state.formState.values as PickerType)?.region?.id) {
              getWorkzones((state.formState.values as PickerType).region.id);
            }
          },
        }}
      >
        {({ invalid, handleSubmit, dirty, values, form }) => {
          formRef.current = form;
          const pickerValues = values as PickerType;
          const currentVehicleType =
            pickerValues?.vehicle?.type ??
            vehicle?.type ??
            VEHICLE_TAGS.BICYCLE;
          const allRequiredFilesLoaded = hasRequiredPickerFilesLoaded(
            pendingUserAdminPicker,
            currentVehicleType
          );
          let saveBlockedBySaveSchema = false;
          if (documentsSaveValidationSchema) {
            try {
              documentsSaveValidationSchema.validateSync(
                pickerValues as object,
                { abortEarly: false }
              );
            } catch {
              saveBlockedBySaveSchema = true;
            }
          }

          const saveBlockedByMissingMotorizedFiles =
            showMotorizedFileReplaceReminder &&
            VEHICLE_MOTORIZED_DOCUMENT_TAGS.some(
              (tag) =>
                !files.vehicle.content.some(
                  (c) =>
                    normalizePickerFileTag(c.tag) === normalizePickerFileTag(tag) &&
                    doesPickerFileRowIndicateUpload(c as PickerFileApiRow)
                )
            );
          const isMotorized =
            MOTORIZED_VEHICLES_TAGS.includes(currentVehicleType);
          const vehicleDataProps: VehicleDataProps = {
            ...props,
            pickerId: id,
            isActivePicker: active,
            testId: "vehicle-data",
            vehicleType: currentVehicleType,
            showMotorizedFileReplaceReminder,
            isMotorized,
            files: files.vehicle,
          };
          const vehicleData: CountryComponentType = {
            AR: <VehicleDataAR {...vehicleDataProps} />,
            CO: <VehicleDataCO {...vehicleDataProps} />,
            MX: <VehicleDataMX {...vehicleDataProps} />,
            CL: <VehicleDataCL {...vehicleDataProps} />,
          };
          return (
            <form onSubmit={handleSubmit}>
              <FormSpy
                subscription={{
                  dirty: true,
                  values: true,
                  invalid: true,
                }}
                onChange={(pro) => {
                  setDirty(pro.dirty);
                  setHasSaved(!pro.dirty);
                  const curr = (pro.values as PickerType)?.vehicle?.type;
                  const prev = previousVehicleTypeRef.current;
                  if (curr !== prev) {
                    const fromType = prev;
                    if (!id) {
                      previousVehicleTypeRef.current = curr;
                      return;
                    }
                    if (vehicleFormSpyHydratedForPickerIdRef.current !== id) {
                      previousVehicleTypeRef.current = curr;
                      return;
                    }
                    previousVehicleTypeRef.current = curr;

                    if (curr != null && MOTORIZED_VEHICLES_TAGS.includes(curr)) {
                      const willShowReminder =
                        (isMotorizedTypeSwap(fromType, curr) &&
                          originalVehicleTypeFromServerRef.current !== VEHICLE_TAGS.BICYCLE &&
                          curr !== originalVehicleTypeFromServerRef.current) ||
                        (isFromNonMotorizedToMotorized(fromType, curr) &&
                          curr !== originalVehicleTypeFromServerRef.current);

                      const alreadyResetThisType =
                        vehicleTypesResetInSessionRef.current.has(curr);

                      const shouldReset = willShowReminder && !alreadyResetThisType;

                      if (shouldReset) {
                        vehicleTypesResetInSessionRef.current.add(curr);
                        ensureMotorizedVehicleFileSlots({
                          vehicleType: curr,
                          fromType: fromType as VehicleTagType | undefined,
                          resetExistingSlots: true,
                        });
                      } else {
                        ensureMotorizedVehicleFileSlots({
                          vehicleType: curr,
                          fromType: fromType as VehicleTagType | undefined,
                          resetExistingSlots: false,
                        });
                      }
                    }
                    if (
                      !curr ||
                      !MOTORIZED_VEHICLES_TAGS.includes(curr)
                    ) {
                      setShowMotorizedFileReplaceReminder(false);
                    } else if (
                      isMotorizedTypeSwap(fromType, curr) &&
                      originalVehicleTypeFromServerRef.current !==
                        VEHICLE_TAGS.BICYCLE &&
                      curr !== originalVehicleTypeFromServerRef.current
                    ) {
                      setShowMotorizedFileReplaceReminder(true);
                    } else if (
                      isFromNonMotorizedToMotorized(fromType, curr) &&
                      curr !== originalVehicleTypeFromServerRef.current
                    ) {
                      setShowMotorizedFileReplaceReminder(true);
                    } else if (
                      curr === originalVehicleTypeFromServerRef.current
                    ) {
                      setShowMotorizedFileReplaceReminder(false);
                    }
                    if (
                      isMotorizedTypeSwap(fromType, curr) ||
                      isFromNonMotorizedToMotorized(fromType, curr)
                    ) {
                      const formCh = form as {
                        change: (name: string, value?: unknown) => void;
                      };
                      const vehicleVals = (pro.values as PickerType).vehicle;
                      if (
                        fromType &&
                        MOTORIZED_VEHICLES_TAGS.includes(fromType)
                      ) {
                        vehicleMotorizedFieldsByTypeRef.current[fromType] =
                          snapshotFromVehicleValues(vehicleVals);
                      }
                      const savedForDestination =
                        curr &&
                        vehicleMotorizedFieldsByTypeRef.current[curr];
                      if (savedForDestination) {
                        applyMotorizedVehicleFieldSnapshot(
                          formCh,
                          savedForDestination
                        );
                      } else {
                        clearMotorizedVehicleFieldValues(formCh);
                      }
                    }
                  }
                }}
              />
              <PersonalData
                {...props}
                pickerId={id}
                regions={regions}
                isActivePicker={active}
                files={files.personalData}
                identificationType={personalData.identification?.name}
                testId="personal-data"
                phoneNumber={phoneNumber}
                name={nameDisplay}
                pickerTermsStatus={props.pickerTermsStatus}
                personalDataEmailRef={personalDataEmailRef}
                setEmailOrUsernameDuplicated={setEmailOrUsernameDuplicated}
              />
              <h3
                data-testId="picker-title-account-data"
                className="detail-picker-section-title"
              >
                {t("picker:accountTitle")}
              </h3>
              {accountData[countryCode]}
              <>
                <h3
                  data-testId="picker-title-vehicle-data"
                  className="detail-picker-section-title"
                >
                  {t("picker:vehicleTitle")}
                </h3>
                {vehicleData[countryCode]}
              </>
              {active ? (
                <div className="active-bottom-container">
                  <div className="toggles-container">
                    <div className="switch-container">
                      <h3 className="switch-title" data-testId="active-picker-state-label">{t("picker:state")}</h3>
                      <Field
                        dataTestId="active-picker-state-switch"
                        name="enable"
                        component={ToggleSwitch}
                      />
                    </div>
                    <div className="switch-container">
                      <h3 className="switch-title" data-testId="fullranking-picker-state-label">{t("picker:pickerFull")}</h3>
                      <Field
                        dataTestId="fullranking-picker-state-switch"
                        name="ranking.fullRanking"
                        component={ToggleSwitch}
                      />
                    </div>
                  </div>
                  <div className="picker-buttons-container">
                    <div className="picker-button">
                      <Button
                        type="button"
                        testId="active-picker-button-restore"
                        onClick={() => cancel(dirty, form)}
                        disabled={!dirty || hasSaved}
                        label={t("global:restore")}
                        size="large"
                        hierarchy="secondary"
                      />
                    </div>
                    <div className="picker-button">
                      <Button
                        type="submit"
                        testId="active-picker-button-save"
                        disabled={!dirty || hasSaved || saveBlockedBySaveSchema || saveBlockedByMissingMotorizedFiles}
                        label={t("global:save")}
                        size="large"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="pending-bottom-container">
                  <div className="picker-buttons-container">
                    <div className="picker-button">
                      <Button
                        type="button"
                        testId="pending-picker-button-save"
                        onClick={() => savePicker(values as PickerType)}
                        disabled={!dirty || hasSaved || saveBlockedBySaveSchema || saveBlockedByMissingMotorizedFiles}
                        label={t("global:saveChanges")}
                        size="medium"
                        width="220"
                        hierarchy="secondary"
                      />
                    </div>
                    <Tooltip
                      message={t("picker:approveTooltip")}
                      disabled={
                        invalid ||
                        wrongFiles ||
                        !allRequiredFilesLoaded ||
                        !dirty ||
                        hasSaved
                      }
                      position={ToolTipPosition.top}
                    >
                      <div className="picker-button">
                        <Button
                          type="submit"
                          testId="active-picker-button-approve"
                          disabled={
                            invalid ||
                            wrongFiles ||
                            !allRequiredFilesLoaded ||
                            !hasSaved
                          }
                          label={t("picker:approvePicker")}
                          size="medium"
                          width="220"
                          hierarchy="primary"
                        />
                      </div>
                    </Tooltip>
                  </div>
                </div>
              )}
            </form>
          );
        }}
      </Form>
    </>
  );
};
